import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { UserFilters, StatusFilterCounts } from "@/types";

interface FilterChipsProps {
  filters: UserFilters;
  counts?: StatusFilterCounts;
  onFilterChange: (filters: UserFilters) => void;
  loading?: boolean;
  error?: Error | null;
}

interface FilterChip {
  label: string;
  value: string;
  count: number;
  type: "kyc" | "deletion";
}

export function FilterChips({
  filters,
  counts,
  onFilterChange,
  loading = false,
  error = null,
}: FilterChipsProps) {
  const chips: FilterChip[] = [
    {
      label: "All",
      value: "all",
      count: counts
        ? (counts.kyc_pending || 0) +
          (counts.kyc_completed || 0) +
          (counts.kyc_failed || 0) +
          (counts.kyc_cancelled || 0)
        : 0,
      type: "kyc",
    },
    {
      label: "Phone Verified",
      value: "kyc_pending",
      count: counts?.kyc_pending || 0,
      type: "kyc",
    },
    {
      label: "KYC Verified",
      value: "kyc_completed",
      count: counts?.kyc_completed || 0,
      type: "kyc",
    },
    {
      label: "Failed",
      value: "kyc_failed",
      count: counts?.kyc_failed || 0,
      type: "kyc",
    },
    {
      label: "Cancelled",
      value: "kyc_cancelled",
      count: counts?.kyc_cancelled || 0,
      type: "kyc",
    },
    {
      label: "Deleted",
      value: "soft_deleted",
      count: counts?.soft_deleted || 0,
      type: "deletion",
    },
  ];

  const hasActiveFilters =
    (filters.kyc_status && filters.kyc_status.length > 0) ||
    (filters.deletion_status && filters.deletion_status.length > 0);

  const isChipActive = (chip: FilterChip): boolean => {
    if (chip.value === "all") {
      return !hasActiveFilters;
    }

    if (chip.type === "deletion") {
      return (
        filters.deletion_status?.includes(
          chip.value as "active" | "pending_deletion" | "soft_deleted",
        ) || false
      );
    }

    return (
      filters.kyc_status?.includes(
        chip.value as
          | "kyc_pending"
          | "kyc_completed"
          | "kyc_failed"
          | "kyc_cancelled",
      ) || false
    );
  };

  const handleChipClick = (chip: FilterChip) => {
    if (chip.value === "all") {
      const newFilters = {
        kyc_status: [],
        deletion_status: [],
      };
      onFilterChange(newFilters);
      return;
    }

    if (chip.type === "deletion") {
      const currentStatuses = filters.deletion_status || [];
      const chipValue = chip.value as
        | "active"
        | "pending_deletion"
        | "soft_deleted";
      const newStatuses = currentStatuses.includes(chipValue)
        ? currentStatuses.filter((s) => s !== chip.value)
        : [...currentStatuses, chipValue];

      const newFilters = {
        ...filters,
        deletion_status: newStatuses,
      };
      onFilterChange(newFilters);
    } else {
      const currentStatuses = filters.kyc_status || [];
      const chipValue = chip.value as
        | "kyc_pending"
        | "kyc_completed"
        | "kyc_failed"
        | "kyc_cancelled";
      const newStatuses = currentStatuses.includes(chipValue)
        ? currentStatuses.filter((s) => s !== chip.value)
        : [...currentStatuses, chipValue];

      const newFilters = {
        ...filters,
        kyc_status: newStatuses,
      };
      onFilterChange(newFilters);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    chip: FilterChip,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleChipClick(chip);
    }
  };

  const handleClearFilters = () => {
    onFilterChange({
      kyc_status: [],
      deletion_status: [],
    });
  };

  if (error) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-red-600 dark:text-red-400">
          Failed to load filter counts. Showing filters without counts.
        </div>
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <Button
              key={chip.value}
              variant={isChipActive(chip) ? "default" : "outline"}
              size="sm"
              onClick={() => handleChipClick(chip)}
              onKeyDown={(e) => handleKeyDown(e, chip)}
              disabled={loading}
              role="switch"
              aria-pressed={isChipActive(chip)}
              aria-label={`${chip.label} filter`}
              className="gap-2"
            >
              {chip.label}
              <Badge variant={isChipActive(chip) ? "secondary" : "outline"}>
                {chip.count}
              </Badge>
            </Button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <Button
            key={chip.value}
            variant={isChipActive(chip) ? "default" : "outline"}
            size="sm"
            onClick={() => handleChipClick(chip)}
            onKeyDown={(e) => handleKeyDown(e, chip)}
            disabled={loading}
            role="switch"
            aria-pressed={isChipActive(chip)}
            aria-label={`${chip.label} filter`}
            className="gap-2"
          >
            {chip.label}
            <Badge variant={isChipActive(chip) ? "secondary" : "outline"}>
              {chip.count}
            </Badge>
          </Button>
        ))}
      </div>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          disabled={loading}
          className="gap-2"
        >
          <X className="h-4 w-4" />
          Clear filters
        </Button>
      )}
    </div>
  );
}
